import { useEffect, useState } from "react";
import { Timer, Trophy, Users } from "lucide-react";
import { fmtChips } from "../../lib/api";
import { AssetImage } from "../AssetImage";
import { getAsset } from "../../assets/assetManifest";
import { AVATAR_CATEGORIES, avatarSrc } from "../../lib/avatars";
import { ChatBubbles } from "./ChatBubbles";

const mmss = (ms) => { const s = Math.max(0, Math.ceil(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const fallbackAvatar = (name) => {
  const people = [...AVATAR_CATEGORIES[0].items, ...AVATAR_CATEGORIES[1].items];
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return people[h % people.length].url;
};
// holders sit on an ellipse around the coin, first seat at the top, clockwise
const holderPos = (i, n) => {
  const a = -Math.PI / 2 + (i / n) * 2 * Math.PI;
  return { left: `${50 + 36 * Math.cos(a)}%`, top: `${53 + 33 * Math.sin(a)}%` };
};

const Holder = ({ seat, isMe, canSit, onSit, onInfo }) => {
  const filled = !!seat.user_id;
  const inner = (
    <div className="relative w-[112px] h-[112px]">
      <AssetImage id="seat_glow_ring" width={216} className={`absolute -inset-[52px] w-[216px] h-[216px] max-w-none seat-glow ${filled ? "opacity-100" : "opacity-70"}`} />
      <div className="absolute inset-0 rounded-full bg-slate-950/90 border border-amber-300/30 shadow-[inset_0_0_30px_rgba(0,0,0,0.9)] overflow-hidden">
        {filled ? (
          <img src={seat.avatar ? avatarSrc(seat.avatar) : fallbackAvatar(seat.name)} alt={seat.name} data-testid={`sng-holder-${seat.index}-avatar`} className="w-full h-full object-cover holder-pop" />
        ) : (
          <AssetImage id="sng_wait_coin" width={112} className="w-full h-full opacity-[0.14] grayscale" />
        )}
      </div>
    </div>
  );
  return (
    <div data-testid={`sng-holder-${seat.index}`} data-filled={filled ? "true" : "false"} className="absolute flex flex-col items-center gap-2 -translate-x-1/2 -translate-y-1/2" style={holderPos(seat.index, seat.total)}>
      {filled ? <button data-testid={`sng-holder-${seat.index}-info`} onClick={() => onInfo?.(seat.user_id)} title={isMe ? "Your player info" : `${seat.name} — player info`} className="rounded-full hover:brightness-110">{inner}</button> : (
        <button data-testid={`seat-${seat.index}-empty`} onClick={() => onSit(seat.index)} disabled={!canSit} aria-label={canSit ? `Register here, seat ${seat.index + 1}` : "Open seat"}
          className="btn-lift rounded-full disabled:opacity-60 hover:drop-shadow-[0_0_18px_rgba(251,191,36,0.7)]">{inner}</button>
      )}
      <div data-testid={`sng-holder-${seat.index}-label`} className={`glass rounded-md px-3 py-1 text-center min-w-[120px] ${filled ? "border-emerald-400/30" : "border-white/10"}`}>
        <div className="text-[11px] font-medium truncate max-w-[140px]">{filled ? `${seat.name}${isMe ? " (you)" : ""}` : "Open seat"}</div>
        <div className={`text-[9px] uppercase tracking-[0.25em] ${filled ? "text-emerald-300" : "text-amber-200/70"}`}>{filled ? "Locked in" : canSit ? "Tap to register" : "Waiting"}</div>
      </div>
    </div>
  );
};

// 2D waiting room shown while a Sit N Go is registering / counting down: spinning Players Room coin in the middle,
// one avatar holder per seat around it; the table itself appears once the tournament starts.
export const SngWaitingRoom = ({ state, t, serverNow, onSit, onInfo, chat = [] }) => {
  const [, tick] = useState(0);
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 1000); return () => clearInterval(i); }, []);
  const now = serverNow();
  const n = state.seats.length;
  const starting = t.status === "STARTING";
  return (
    <div data-testid="sng-waiting-room" data-status={t.status} className="relative w-full h-full noise rounded-3xl overflow-hidden border border-amber-300/20 shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
      style={{ backgroundImage: `url(${getAsset("bg_lobby_dark").path})`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-3 z-10">
        <AssetImage id="sng_wait_coin" width={170} className="sng-coin drop-shadow-[0_18px_40px_rgba(0,0,0,0.75)]" data-testid="sng-wait-coin" />
        <div data-testid="sng-waiting-status" className="glass rounded-full px-4 py-1.5 text-sm flex items-center gap-2 text-amber-100 whitespace-nowrap">
          {starting ? <><Timer className="w-4 h-4 text-amber-300" /> Starting in <span className="font-mono">{mmss(t.start_at - now)}</span></>
            : <><Users className="w-4 h-4 text-emerald-300" /> {t.registered}/{t.seats} registered · starts when full</>}
        </div>
      </div>
      <div data-testid="sng-waiting-info" className="absolute left-5 top-5 glass rounded-xl px-4 py-3 text-xs text-amber-100 flex flex-col gap-1 z-10">
        <div className="flex items-center gap-1.5 font-display tracking-wider text-amber-300"><Trophy className="w-3.5 h-3.5" /> SIT N GO</div>
        <div>Buy-in <span className="font-mono">{fmtChips(t.buy_in)}</span> + <span className="font-mono">{fmtChips(t.fee)}</span> fee</div>
        <div>Prize pool <span className="font-mono text-emerald-300">{fmtChips(t.buy_in * t.seats)}</span></div>
        <div>Starting stack <span className="font-mono">{fmtChips(t.starting_stack)}</span></div>
      </div>
      {state.seats.map((s) => <Holder key={s.index} seat={{ ...s, total: n }} isMe={s.index === state.my_seat} canSit={state.my_seat == null && !starting} onSit={onSit} onInfo={onInfo} />)}
      <ChatBubbles chat={chat} me={state.my_seat} at={(i) => holderPos(i, n)} />
    </div>
  );
};
